import { useCallback, useEffect, useRef, useState } from "react";
import { listSavedQueries } from "../QueryBuilder/queryBuilderApi";
import {
  createReminderRule,
  deleteReminderRule,
  listReminderRules,
  updateReminderRule,
} from "./remindersApi";
import { buildRulePayload } from "./reminderConstants";

/**
 * Owns the reminders page data: the company's reminder rules and the saved
 * queries a rule can be built on, plus create/toggle/delete actions. The list
 * fetch is race-guarded so a stale response can't overwrite a newer one.
 * @returns {object} reminders state and actions.
 */
export function useReminders() {
  const [rules, setRules] = useState([]);
  const [savedQueries, setSavedQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  const refresh = useCallback(() => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    Promise.all([listReminderRules(), listSavedQueries()]).then(
      ([rulesRes, sqRes]) => {
        if (fetchId !== fetchIdRef.current) return;
        if (rulesRes.isSuccess && Array.isArray(rulesRes.data)) {
          setRules(rulesRes.data);
        } else {
          setError(rulesRes.message || "Failed to load reminder rules");
        }
        if (sqRes.isSuccess && Array.isArray(sqRes.data)) {
          setSavedQueries(sqRes.data);
        }
        setLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Creates a rule from form state, then refreshes.
   * @param {object} form the rule form state.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const create = useCallback(
    async (form) => {
      const res = await createReminderRule(buildRulePayload(form));
      if (res.isSuccess) refresh();
      return { ok: !!res.isSuccess, message: res.message };
    },
    [refresh],
  );

  /**
   * Saves edits to an existing rule, then refreshes.
   * @param {number} id the rule id.
   * @param {object} form the rule form state.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const update = useCallback(
    async (id, form) => {
      const res = await updateReminderRule(id, buildRulePayload(form));
      if (res.isSuccess) refresh();
      return { ok: !!res.isSuccess, message: res.message };
    },
    [refresh],
  );

  /**
   * Flips a rule's enabled state, then refreshes.
   * @param {object} rule the rule to toggle.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const toggle = useCallback(
    async (rule) => {
      const res = await updateReminderRule(rule.id, {
        isEnabled: !rule.isEnabled,
      });
      if (res.isSuccess) refresh();
      return { ok: !!res.isSuccess, message: res.message };
    },
    [refresh],
  );

  /**
   * Deletes a rule, then refreshes.
   * @param {number} id the rule id.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const remove = useCallback(
    async (id) => {
      const res = await deleteReminderRule(id);
      if (res.isSuccess) refresh();
      return { ok: !!res.isSuccess, message: res.message };
    },
    [refresh],
  );

  return {
    rules,
    savedQueries,
    loading,
    error,
    create,
    update,
    toggle,
    remove,
  };
}
